import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import { Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  Mutation,
  MutationGenerateAttachmentPresignedUrlArgs,
  MutationUploadContactAttachmentArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useTakeNativeCameraPhoto } from 'src/components/domain/capacitor/hooks/use-take-native-camera-photo';
import { OptionsBottomDrawer } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { NotificationContext } from 'src/components/general/feedback/hooks/use-show-notification';
import { Icon } from 'src/components/general/icon';
import { Checkbox } from 'src/components/general/inputs/checkbox';
import { AttachmentGhostInput } from 'src/components/general/inputs/ghost-input';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { uploadFileToS3 } from 'src/utils/upload-file-to-s3';
import { FlexColumn } from '../shared';
import { ContactAttachmentItem } from './contact-attachment-item';
import {
  GENERATE_ATTACHMENT_PRESIGNED_POST,
  GET_CONTACT_ATTACHMENTS,
  UPLOAD_ATTACHMENT,
} from './queries';

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB

const { Dragger } = Upload;

const ListContainer = styled.div`
  padding: 0 20px;
`;

const StyledDragger = styled(Dragger)`
  border-radius: 4px !important;
  background: ${({ theme }) => theme.colors.white} !important;

  :hover {
    border-color: ${({ theme }) => theme.colors.flint} !important;
  }
  & .upload-icon {
    margin-right: 10px;
  }

  & span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.flint};
  }

  & path {
    fill: ${({ theme }) => theme.colors.flint};
  }

  & .ant-upload-text {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .ant-upload {
    padding: 8px 15px !important;
  }
`;

const StyledUploadButton = styled.button`
  border-radius: 4px !important;
  border: 1px dashed ${({ theme }) => theme.colors.flint};
  background: transparent;
  width: 100%;
  padding: 7px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.flint} !important;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  & .upload-icon {
    margin-right: 10px;
  }

  & span {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.flint};
  }

  & path {
    fill: ${({ theme }) => theme.colors.flint};
  }

  & .ant-upload-text {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StyledUpload = styled(Upload)`
  display: none;
`;

export const ContactAttachments: React.FC<{ contactId: number }> = ({ contactId }) => {
  const { t } = useTranslation(['contacts', 'common']);
  const { openNotification } = useContext(NotificationContext);
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const showConfirmationModal = useShowConfirmationModal();
  const takeNativeCameraPhoto = useTakeNativeCameraPhoto();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, refetch } = useQuery<any, any>(GET_CONTACT_ATTACHMENTS, {
    variables: {
      first: 100,
      contactId,
    },
    onError: handleGraphQLError,
  });

  const [generateAttachmentPresignedUrl] = useMutation<
    Pick<Mutation, 'generateAttachmentPresignedUrl'>,
    MutationGenerateAttachmentPresignedUrlArgs
  >(GENERATE_ATTACHMENT_PRESIGNED_POST);

  const [uploadContactAttachment] = useMutation<
    Pick<Mutation, 'uploadContactAttachment'>,
    MutationUploadContactAttachmentArgs
  >(UPLOAD_ATTACHMENT, { onError: handleGraphQLError });

  // batching and conflict state
  const batchRef = useRef<File[]>([]);
  const timerRef = useRef<number>();
  const [isUploading, setIsUploading] = useState(false);

  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const breakpointUpMd = useBreakpoint(up('md'));

  // Handle mobile file selection
  const handleMobileFileSelect = (type: 'camera' | 'upload') => {
    setShowMobileOptions(false);

    if (type === 'camera') {
      void handleCameraCapture();
    } else {
      inputRef.current?.click();
    }
  };

  // Handle camera capture using Capacitor
  const handleCameraCapture = async () => {
    const cameraHandler = match(Capacitor.isNativePlatform())
      .with(true, () => {
        return async () => {
          const file = await takeNativeCameraPhoto();

          if (file) {
            void processBatch([file]);
          }
        };
      })
      .otherwise(() => {
        return () => {
          inputRef.current?.click();
        };
      });

    await cameraHandler();
  };

  // Handle click on the upload area
  const handleUploadClick = (e: React.MouseEvent) => {
    if (!breakpointUpMd) {
      e.preventDefault();
      e.stopPropagation();
      setShowMobileOptions(true);
    }
  };

  const runUploads = useCallback(
    async (
      items: Array<{ file: File; presigned: any }> | File[],
      forceOverwrite: boolean,
    ) => {
      const tasks = (items as any[]).map(async (item) => {
        let file: File;
        let presignedPost: any;
        if ((item as any).presigned) {
          file = (item as any).file;
          presignedPost = (item as any).presigned;
        } else {
          file = item as File;
          const { data } = await generateAttachmentPresignedUrl({
            onError: handleGraphQLError,
            variables: {
              input: {
                institutionId: currentInstitution.id,
                contactId: contactId,
                filename: file.name,
                mimeType: file.type,
                sizeBytes: file.size,
                forceOverwrite,
              },
            },
          });
          presignedPost = data!.generateAttachmentPresignedUrl!;
        }
        const path = presignedPost.fields.find((f: any) => f.key === 'key')!.value;
        const url = await uploadFileToS3(file, presignedPost);

        if (!url) {
          openNotification({
            type: 'error',
            title: t('contacts:attachments.errors.uploadFailed'),
          });
          return;
        }

        await uploadContactAttachment({
          variables: {
            contactId: contactId,
            attachment: {
              path,
              url: url ?? '',
              filename: file.name,
              mimeType: file.type,
              sizeBytes: file.size,
            },
          },
        });
        openNotification({
          type: 'success',
          title: t('contacts:attachments.uploadSuccess'),
        });
      });
      await Promise.all(tasks);
    },
    [
      contactId,
      currentInstitution.id,
      generateAttachmentPresignedUrl,
      handleGraphQLError,
      openNotification,
      t,
      uploadContactAttachment,
    ],
  );

  const showNextConflict = useCallback(
    async (queue: File[]) => {
      if (!queue.length) {
        setIsUploading(false);
        await refetch();
        return;
      }
      const [file, ...rest] = queue;
      const moreThanOne = rest.length > 0;
      let isChecked = false;

      showConfirmationModal({
        title: t('contacts:attachments.duplicatedFileModal.title'),
        message: (
          <FlexColumn>
            <span>
              {t('contacts:attachments.duplicatedFileModal.body', {
                filename: file.name,
              })}
            </span>
            <br />
            <span>{t('contacts:attachments.duplicatedFileModal.body2')}</span>
            <br />
            {moreThanOne && (
              <span>
                <Checkbox
                  data-lgg-id="duplicated-file-apply-to-all-checkbox"
                  onChange={(e) => {
                    isChecked = e.target.checked;
                  }}
                >
                  {t('contacts:attachments.duplicatedFileModal.applyToAllText')}
                </Checkbox>
              </span>
            )}
          </FlexColumn>
        ),
        confirmButtonText: t(
          'contacts:attachments.duplicatedFileModal.confirmButtonText',
        ),
        cancelButtonText: t('contacts:attachments.duplicatedFileModal.cancelButtonText'),
        confirmButtonType: 'primary',
        onConfirm: async () => {
          if (isChecked) {
            await runUploads([file, ...rest], true);
            setIsUploading(false);
            await refetch();
          } else {
            await runUploads([file], true);
          }
        },
        onCancel: async () => {
          await showNextConflict(rest);
        },
        onClose: async (props?: { isSuccess: boolean }) => {
          if (props?.isSuccess && isChecked) {
            await showNextConflict([]);
          } else {
            await showNextConflict(rest);
          }
        },
        testId: 'duplicated-file-modal',
        width: 'unset',
      });
    },
    [showConfirmationModal, t, refetch, runUploads],
  );

  const processBatch = useCallback(
    async (files: File[] | RcFile[]) => {
      setIsUploading(true);

      // 1) filter out oversized files immediately
      const validFiles = files.filter((f) => {
        if (f.size > MAX_FILE_SIZE) {
          openNotification({
            type: 'error',
            title: t('contacts:attachments.errors.general'),
            message: t('contacts:attachments.errors.fileSizeExceeded'),
          });
          return false;
        }
        return true;
      });

      // 2) dry-run presigned URL in parallel, capturing conflicts
      const presignedPromises = validFiles.map((f) =>
        generateAttachmentPresignedUrl({
          variables: {
            input: {
              institutionId: currentInstitution.id,
              contactId: contactId,
              filename: f.name,
              mimeType: f.type,
              sizeBytes: f.size,
              forceOverwrite: false,
            },
          },
        })
          .then((res) => {
            if (res.errors) {
              return Promise.reject({ err: res.errors, file: f });
            }

            return {
              file: f,
              presigned: res.data!.generateAttachmentPresignedUrl!,
            };
          })
          .catch((err) => Promise.reject({ err, file: f })),
      );

      const results = await Promise.allSettled(presignedPromises);
      const toUpload: Array<{ file: File; presigned: any }> = [];
      const conflicts: File[] = [];

      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          toUpload.push(r.value);
        } else {
          const { err, file } = r.reason as any;
          const code = err?.graphQLErrors?.[0]?.extensions?.code;

          if (code === 'FILE_VALIDATION:FILE_ALREADY_EXISTS_BY_YOU') {
            conflicts.push(file);
          } else if (code === 'FILE_VALIDATION:FILE_ALREADY_EXISTS_BY_OTHER') {
            openNotification({
              type: 'error',
              title: t('contacts:attachments.errors.fileAlreadyExistsByOther.title'),
              message: t('contacts:attachments.errors.fileAlreadyExistsByOther.message', {
                filename: file.name,
              }),
            });
          } else {
            openNotification({
              type: 'error',
              title: t('contacts:attachments.errors.couldNotGetPresignedUrl'),
            });
          }
        }
      });

      // 3) upload all non-conflicted files immediately
      await runUploads(toUpload, false);

      // 4) handle conflicts one-by-one or all-at-once
      if (conflicts.length) {
        await showNextConflict(conflicts);
      } else {
        setIsUploading(false);
        void refetch();
      }
    },
    [
      contactId,
      currentInstitution.id,
      generateAttachmentPresignedUrl,
      openNotification,
      refetch,
      runUploads,
      showNextConflict,
      t,
    ],
  );

  const beforeUpload = useCallback(
    (file: File) => {
      batchRef.current.push(file);

      if (!timerRef.current) {
        timerRef.current = window.setTimeout(() => {
          void processBatch(batchRef.current);
          batchRef.current = [];
          timerRef.current = undefined;
        }, 0);
      }
      return false;
    },
    [processBatch],
  );

  const attachmentsList = data?.contact?.attachments?.nodes ?? [];

  const fileInputProps = useMemo(
    () => ({
      name: 'file',
      'data-lgg-id': 'contact-file-upload-input',
      multiple: true,
      showUploadList: false,
      disabled: isUploading,
      beforeUpload: beforeUpload,
    }),
    [beforeUpload, isUploading],
  );

  return (
    <ListContainer>
      {attachmentsList.map((attachment) => (
        <ContactAttachmentItem
          key={attachment.id}
          id={attachment.id}
          attachment={attachment}
          onDeleted={() => refetch()}
        />
      ))}
      {breakpointUpMd ? (
        <StyledDragger {...fileInputProps}>
          <span className="ant-upload-text" data-lgg-id="contact-file-upload-target">
            <Icon className="upload-icon" type="upload" />
            {t('contacts:attachments.dropFiles')}
          </span>
        </StyledDragger>
      ) : (
        <>
          <StyledUpload {...fileInputProps}>
            <AttachmentGhostInput ref={inputRef} />
          </StyledUpload>
          <StyledUploadButton
            type="button"
            disabled={isUploading}
            onClick={handleUploadClick}
          >
            <span className="ant-upload-text">
              <Icon className="upload-icon" type="upload" />
              {t('contacts:attachments.dropFilesMobile')}
            </span>
          </StyledUploadButton>
        </>
      )}

      {!breakpointUpMd && (
        <OptionsBottomDrawer
          visible={showMobileOptions}
          onClose={() => setShowMobileOptions(false)}
          title={t('contacts:attachments.mobileUploads.title')}
          options={[
            {
              label: t('contacts:attachments.mobileUploads.upload'),
              icon: 'upload',
              onClick: () => handleMobileFileSelect('upload'),
            },
            {
              label: t('contacts:attachments.mobileUploads.takePhoto'),
              icon: 'camera',
              onClick: () => handleMobileFileSelect('camera'),
            },
          ]}
        />
      )}
    </ListContainer>
  );
};
